import React from "react";

import { appURLs } from "@/app/app-urls.mjs";
import type { GameDefinition } from "@/app/game-definition.mjs";
import { OVERLAY_IDS } from "@/game-apex/constants.mjs";
import { GAME_SYMBOL_APEX } from "@/game-apex/definition-symbol.mjs";
import type { Legends } from "@/game-apex/models/legends.mjs";
import type { ApexMatch, LiveMatch } from "@/game-apex/models/match.mjs";
import type { Matchlist } from "@/game-apex/models/matches.mjs";
import type { ApexPlayer } from "@/game-apex/models/player.mjs";
import type { PlayerLegendStats } from "@/game-apex/models/player-legend-stats.mjs";
import type { ApexPlayerStats } from "@/game-apex/models/player-stats.mjs";
import type { ApexPlayerWeaponStatsMap } from "@/game-apex/models/player-weapon-stats.mjs";
import type { Seasons } from "@/game-apex/models/seasons.mjs";
import type { ApexWeapons } from "@/game-apex/models/weapon.mjs";
import routes from "@/game-apex/routes.mjs";
import type { GameOverlayFeature } from "@/hub-overlays/constants.mjs";
import GameIconBoxApex from "@/inline-assets/game-icon-box-apex.svg";
import FriendGameIconBoxApex from "@/inline-assets/game-icon-friend-box-apex.svg";
import GameShapeApex from "@/inline-assets/GameShapeApex.svg";

export { GAME_SYMBOL_APEX };

const APEX_OVERLAY_SETTINGS = {
  isCombatOverlayEnabled: true,
  isBenchmarkOverlayEnabled: true,
  isLegendSelectOverlayEnabled: true,
  apexPostmatchOverlayToggleKey: {
    value: "Tab",
    modifiers: {
      ctrlKey: true,
      altKey: false,
      shiftKey: false,
    },
  },
  dynamicStats: {
    damageDealt: true,
    damageStreak: true,
    damageTaken: true,
    kills: true,
    assists: true,
    loot: true,
    accuracy: true,
    headshot: true,
    benchmarkOption: 0,
  },
  positions: Object.values(OVERLAY_IDS).reduce((acc, id) => {
    acc[id] = { x: 0, y: 0, isDragged: false };
    return acc;
  }, {}),
};

export type ApexOverlaySettings = typeof APEX_OVERLAY_SETTINGS;

type S = ApexOverlaySettings;

const definition = {
  gameSymbol: GAME_SYMBOL_APEX,
  icon: `${appURLs.CDN_PLAIN}/blitz/ui/images/icons/games/small/APEX-small.svg`,
  iconShape: GameShapeApex,
  iconBox: GameIconBoxApex,
  iconBadge: FriendGameIconBoxApex,
  imageHero: `${appURLs.CDN_PLAIN}/blitz/games/assest/hero-logo/hero-logo-apex.png`,
  imageBanner: `${appURLs.CDN}/blitz/library/banner-apex.png`,
  imageKeyart: `${appURLs.CDN}/blitz/ui/img/game-cover/apex-min.webp`,
  imageWordmark: `${appURLs.CDN}/blitz/ui/img/gamecover/name-apex.webp`,
  isActive: true as const,
  fullName: ["common:games.apex.long", "Apex Legends"],
  friendlyName: ["common:games.apex.short", "Apex"],
  preferFriendlyName: false as const,
  shortName: "apex" as const,
  backendName: "APEX" as const,
  gameColor: "#CB2630",

  overlayBackground: {
    url: `${appURLs.CDN}/blitz/ui/img/settings/apexOverlay/apex-background.webp`,
    offset: "center",
  },

  overlays: {
    combat: {
      date: new Date(2023, 1, 16),
      title: ["overlays:apex.dynamicStats.title", "Dynamic Stats"],
      description: [
        "overlays:apex.dynamicStats.description",
        "Track key combat metrics in real time and compare them to the best in the lobby.",
      ],
      key: ["isCombatOverlayEnabled"],
      tags: ["tracker"],
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/apexOverlay/dynamic-stats-overlay.webp`,
        small: `${appURLs.CDN}/blitz/ui/img/settings/apexOverlay/apex-dynamic-stats.webp`,
      },
      features: [
        {
          text: [
            "overlays:apex.dynamicStats.options.displayOptions.title",
            "Display Options",
          ],
          items: [
            {
              text: [
                "overlays:apex.dynamicStats.options.displayOptions.desc",
                "The combat stats you will see within the game.",
              ],
            },
            {
              title: [
                "overlays:apex.dynamicStats.options.displayOptions.damageDealt",
                "Damage Dealt",
              ],
              toggle: { key: ["dynamicStats", "damageDealt"] },
            },
            {
              title: [
                "overlays:apex.dynamicStats.options.displayOptions.damageStreak",
                "Damage Streak",
              ],
              toggle: { key: ["dynamicStats", "damageStreak"] },
            },
            {
              title: [
                "overlays:apex.dynamicStats.options.displayOptions.damageTaken",
                "Damage Taken",
              ],
              toggle: { key: ["dynamicStats", "damageTaken"] },
            },
            {
              title: [
                "overlays:apex.dynamicStats.options.displayOptions.kills",
                "Kills",
              ],
              toggle: { key: ["dynamicStats", "kills"] },
            },
            {
              title: [
                "overlays:apex.dynamicStats.options.displayOptions.assists",
                "Assists",
              ],
              toggle: { key: ["dynamicStats", "assists"] },
            },
            {
              title: [
                "overlays:apex.dynamicStats.options.displayOptions.loot",
                "Loot",
              ],
              toggle: { key: ["dynamicStats", "loot"] },
            },
            {
              title: [
                "overlays:apex.dynamicStats.options.displayOptions.accuracy",
                "Accuracy",
              ],
              toggle: { key: ["dynamicStats", "accuracy"] },
            },
            {
              title: [
                "overlays:apex.dynamicStats.options.displayOptions.headshotPercent",
                "Headshot %",
              ],
              toggle: { key: ["dynamicStats", "headshot"] },
            },
          ],
        },
        {
          text: [
            "overlays:apex.dynamicStats.options.benchmarkOptions.title",
            "Benchmark Options",
          ],
          items: [
            {
              text: [
                "overlays:apex.dynamicStats.options.benchmarkOptions.desc",
                "Who your stats will be compared against in real time.",
              ],
              select: {
                key: ["dynamicStats", "benchmarkOption"],
                style: "radio",
                options: [
                  {
                    value: 0,
                    text: [
                      "overlays:apex.dynamicStats.options.benchmarkOptions.vsTeammates",
                      "vs Teammates",
                    ],
                  },
                  {
                    value: 1,
                    text: [
                      "overlays:apex.dynamicStats.options.benchmarkOptions.vsLobby",
                      "vs Lobby",
                    ],
                  },
                ],
              },
            },
          ],
        },
      ],
    },
    postMatch: {
      date: new Date(2023, 1, 16),
      title: ["overlays:apex.postMatch.title", "Post Match Insights"],
      description: [
        "overlays:apex.postMatch.description",
        "Automatically pop up in-depth statistics as soon as your game ends.",
      ],
      key: ["isBenchmarkOverlayEnabled"],
      tags: ["performance"],
      images: {
        small: `${appURLs.CDN}/blitz/ui/img/settings/apexOverlay/apex-legend-select.webp`,
        large: `${appURLs.CDN}/blitz/ui/img/settings/apexOverlay/post-match-portrait.webp`,
      },
    },
    legendSelect: {
      date: new Date(2023, 1, 16),
      title: ["overlays:apex.legendSelect.title", "Legend Select"],
      description: [
        "overlays:apex.legendSelect.description",
        "Compare your top legends' stats and choose the best option on legend select.",
      ],
      key: ["isLegendSelectOverlayEnabled"],
      tags: ["performance"],
      images: {
        small: `${appURLs.CDN}/blitz/ui/img/settings/apexOverlay/apex-post-match-insights.webp`,
        large: `${appURLs.CDN}/blitz/ui/img/settings/apexOverlay/legend-select-portrait.webp`,
      },
    },
  },

  streamerOverlays: {
    apexStreamer: {
      date: new Date(2023, 1, 16),
      title: ["overlays:streamerOverlay.title", "Streamer Overlay"],
      description: [
        "overlays:streamerOverlay.description",
        "Show your stats in streaming software, such as OBS Studio.",
      ],
      tags: ["utility"],
      noToggle: true,
      images: {
        small: `${appURLs.CDN}/blitz/ui/img/settings/apexOverlay/apex-streamer-overlay-2.webp`,
        large: null,
      },
      Preview: React.lazy(() => import("@/game-apex/StreamerOverlay.jsx")),
      features: async () =>
        (await import("@/game-apex/OverlayStreamerApex.jsx"))
          .default as GameOverlayFeature<S>[],
    },
  },

  overlaySettingsKey: "apexOverlaySettings",
  overlaySettings: APEX_OVERLAY_SETTINGS,

  IPC: () => import("@/game-apex/init.mjs"),

  settings: () => import("@/game-apex/Settings.jsx"),
  routes,

  meta: {
    overlays: {
      title: ["overlays:meta.apex.title", "Apex Legends Overlays"],
      description: [
        "overlays:meta.apex.description",
        "Elevate your Apex Legends experience with our exclusive collection of overlays, designed to enhance your gameplay with real time stats and insights.",
      ],
      subtitle: [
        "overlays:meta.apex.subtitle",
        "Get all the information you need to dominate live with Apex overlays powered by Blitz. Track key performance metrics live compared to the best in the lobby",
      ],
    },
    game: {
      title: [
        "home:meta.home.apex.title",
        "Apex Tracker - The Best Stats, Leaderboards, and Insights with Blitz.gg",
      ],
      description: [
        "home:meta.home.apex.description",
        "Use Blitz.gg's Apex Tracker to track your stats, monitor your performance, and win more games. Download our desktop app for the best tracking tools and premium real-time overlays!",
      ],
    },
    pros: [
      {
        name: "TSM Imperial Hal",
        src: "tsm-imperial-hal-dc02fb.webp",
        id: "1000641526219",
      },
      {
        name: "TSM_MNK",
        src: "tsm-reps-93ef12.webp",
        id: "2250191893",
      },
      {
        name: "TSM Verhulst",
        src: "tsm-verhulst-fdbd98.webp",
        id: "1008946185386",
      },
      {
        name: "Mande",
        src: "mande-4e3064.webp",
        id: "1000170997629",
      },
    ],
    bg: `${appURLs.CDN}/blitz/ui/img/game-bg/apex-9a5a6.webp`,
    players: "25M+",
    matches: "70M+",
    guides: [],
    features: [
      {
        title: ["apex.features.profile-page.title", "Profile Page"],
        description: [
          "apex.features.profile-page.description",
          "Track valuable metrics over time to measure your gaming performance",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/apex/profile-page.webp`,
      },
      {
        title: [
          "apex.features.dynamic-stats-overlay.title",
          "Dynamic Stats Overlay",
        ],
        description: [
          "apex.features.dynamic-stats-overlay.description",
          "Track key combat metrics in real-time to better guage performance",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/apex/dynamic-stats-overlay.webp`,
      },
      {
        title: [
          "apex.features.legend-select-overlay.title",
          "Legend Select Overlay",
        ],
        description: [
          "apex.features.legend-select-overlay.description",
          "Compare your top Legend's stats to choose the best option on Legend select",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/apex/legend-select-overlay.webp`,
      },
      {
        title: [
          "apex.features.post-match-insights-overlay.title",
          "Post Match Insights Overlay",
        ],
        description: [
          "apex.features.post-match-insights-overlay.description",
          "Automatically view and benchmark key stats as soon as your match ends",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/apex/post-match-insights-overlay.webp`,
      },
      {
        title: ["apex.features.weapon-statistics.title", "Weapons Statistics"],
        description: [
          "apex.features.weapon-statistics.description",
          "Follow the most used and effective weapons in Apex Legends with our global stats tracker",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/apex/weapon-statistics.webp`,
      },
      {
        title: ["apex.features.map-statistics.title", "Map Statistics"],
        description: [
          "apex.features.map-statistics.description",
          "Stay ahead with real-time performance metrics for all Apex Legends maps",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/apex/map-statistics.webp`,
      },
      {
        title: ["apex.features.legend-statistics.title", "Legends Statistics"],
        description: [
          "apex.features.legend-statistics.description",
          "Stay on top of Apex Legends trends with our real-time global stats tracker",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/apex/legend-statistics.webp`,
      },
      {
        title: ["apex.features.leaderboards.title", "Leaderboards"],
        description: [
          "apex.features.leaderboards.description",
          "Discover where you stand among the greats with our Apex Legends leaderboard tracker",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/apex/leaderboards.webp`,
      },
      {
        title: ["apex.features.legends-guide.title", "Legends Guide"],
        description: [
          "apex.features.legends-guide.description",
          "Unlock the true potential of your favorite Apex Legend with our comprehensive guide on abilities and strategies",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/apex/legends-guide.webp`,
      },
      {
        title: ["apex.features.weapon-guide.title", "Weapons Guide"],
        description: [
          "apex.features.weapon-guide.description",
          "Discover the Best Guns in Apex Legends: From SMGs to Assault Rifles, we cover them all",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/apex/weapon-guide.webp`,
      },
    ],
    articles: [
      {
        createdAt: new Date("2023-03-15"),
        title: [
          "home:articles.apex.dynamic-stats-overlay.title",
          "Dynamic Stats Overlay",
        ],
        subtitle: [
          "home:articles.apex.dynamic-stats-overlay.subtitle",
          "Track key combat metrics in real time and compare them to your teammates, or the best in the lobby.",
        ],
        background: `${appURLs.CDN}/homepage/news/apex-dynamic-stats.webp`,
        link: {
          href: `/overlays/apex`,
          text: ["common:overlay.view", "View Overlay"],
        },
      },
      {
        createdAt: new Date("2024-11-01"),
        title: [
          "apex:patchNotes.terror.titlePanel",
          "Techno Terror Collection Event",
        ],
        subtitle: [
          "apex:meta.patchNotes.upheaval.description",
          "Have a scary good time with Trick N’ Treat Trios returning, Pathfinder’s Prestige Skin “Apex Mecha”, and see which Legends are dressing up!",
        ],
        background: `${appURLs.CDN}/homepage/news/apex-dynamic-stats.webp`,
        link: {
          href: `/apex/patchnotes`,
          text: ["common:patchNotes.view", "View Patch Notes"],
        },
      },
    ],
  },

  navLinks: [
    {
      href: `/overlays/apex`,
      text: ["common:navigation.overlays", "Overlays"],
      excludeSearch: true,
    },
  ],
} satisfies GameDefinition<typeof GAME_SYMBOL_APEX>;

declare module "@/__main__/app-state.mjs" {
  interface AppState {
    apex: {
      profiles: { [profileId: string]: ApexPlayer };
      matchlists: { [profileId: string]: Matchlist };
      matches: { [matchId: string]: ApexMatch };

      meta: {
        seasons: Seasons;
        weapons: ApexWeapons;
        legends: Legends;
      };

      playerStats: {
        [profileId: string]: {
          [season: keyof Seasons]: {
            [gameMode: string]: ApexPlayerStats;
          };
        };
      };
      playerLegendStats: {
        [profileId: string]: {
          [season: keyof Seasons]: {
            [gameMode: string]: PlayerLegendStats;
          };
        };
      };
      playerWeaponStats: {
        [profileId: string]: {
          [season: keyof Seasons]: {
            [gameMode: string]: ApexPlayerWeaponStatsMap;
          };
        };
      };

      live: null;
      liveGame?: LiveMatch;
      liveGameTime?: Date;
    };
  }
}

export default definition;
